import React, { useState, useEffect, useRef } from 'react';
import { Brain, MessageCircle, BookOpen, RefreshCw, Send, Trophy } from 'lucide-react';

const API_BASE = 'https://study-buddy.vkminjur.workers.dev';
const SESSION_ID = 'user-session-' + Math.random().toString(36).substr(2, 9);

// Type definitions
interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
}

interface Quiz {
  id: string;
  topic: string;
  difficulty: string;
  questions: QuizQuestion[];
  timestamp: string;
  completed: boolean;
  score?: number;
  total?: number;
}

interface QuizResult {
  question: string;
  userAnswer: string;
  correctAnswer: string;
  isCorrect: boolean;
  explanation: string;
}

interface QuizResults {
  score: number;
  total: number;
  percentage: string;
  results: QuizResult[];
}

export default function StudyBuddy() {
  const [mode, setMode] = useState<'chat' | 'quiz'>('chat');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [quizTopic, setQuizTopic] = useState('');
  const [quizDifficulty, setQuizDifficulty] = useState('medium');
  const [currentQuiz, setCurrentQuiz] = useState<Quiz | null>(null);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, string>>({});
  const [quizResults, setQuizResults] = useState<QuizResults | null>(null);
  const [quizHistory, setQuizHistory] = useState<Quiz[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSession();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSession = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/session?sessionId=${SESSION_ID}`);
      const data = await response.json();
      
      if (data.conversationHistory && data.conversationHistory.length > 0) {
        setMessages(data.conversationHistory);
      } else {
        setMessages([{
          role: 'assistant',
          content: '👋 Hi! I\'m your Study Buddy. I can help you understand topics, answer questions, or create quizzes to test your knowledge. What would you like to study today?'
        }]);
      }
      
      if (data.quizHistory) {
        setQuizHistory(data.quizHistory);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      setMessages([{
        role: 'assistant',
        content: '👋 Hi! I\'m your Study Buddy. I can help you understand topics, answer questions, or create quizzes to test your knowledge. What would you like to study today?'
      }]);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage;
    setInputMessage('');
    setLoading(true);

    const newMessages: Message[] = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);

    try {
      const response = await fetch(`${API_BASE}/api/chat?sessionId=${SESSION_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory: messages
        })
      });

      const data = await response.json();
      setMessages(data.conversationHistory);
    } catch (error) {
      setMessages([...newMessages, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const generateQuiz = async () => {
    if (!quizTopic.trim() || loading) return;

    setLoading(true);
    setQuizResults(null);
    setQuizAnswers({});

    try {
      const response = await fetch(`${API_BASE}/api/quiz/generate?sessionId=${SESSION_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: quizTopic,
          difficulty: quizDifficulty,
          numQuestions: 5
        })
      });

      const quiz = await response.json();
      if (quiz.error) {
        alert('Failed to generate quiz: ' + quiz.error);
      } else {
        setCurrentQuiz(quiz);
        setQuizHistory([...quizHistory, quiz]);
      }
    } catch (error) {
      alert('Failed to generate quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (!currentQuiz || loading) return;

    const allAnswered = currentQuiz.questions.every((_, idx) => quizAnswers[idx]);
    if (!allAnswered) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/quiz/check?sessionId=${SESSION_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quizId: currentQuiz.id,
          answers: Object.values(quizAnswers)
        })
      });

      const results = await response.json();
      setQuizResults(results);
    } catch (error) {
      alert('Failed to submit quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetSession = async () => {
    if (!window.confirm('Are you sure you want to reset your study session? This will clear all chat history and quizzes.')) {
      return;
    }

    try {
      await fetch(`${API_BASE}/api/session/reset?sessionId=${SESSION_ID}`, {
        method: 'POST'
      });
      
      setMessages([{
        role: 'assistant',
        content: '👋 Hi! I\'m your Study Buddy. I can help you understand topics, answer questions, or create quizzes to test your knowledge. What would you like to study today?'
      }]);
      setQuizHistory([]);
      setCurrentQuiz(null);
      setQuizResults(null);
    } catch (error) {
      alert('Failed to reset session.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto p-4 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-800">Study Buddy AI</h1>
            </div>
            <button
              onClick={resetSession}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
            >
              <RefreshCw className="w-4 h-4" />
              Reset Session
            </button>
          </div>
          
          {/* Mode Toggle */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setMode('chat')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                mode === 'chat'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              Chat
            </button>
            <button
              onClick={() => setMode('quiz')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
                mode === 'quiz'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Quiz Mode
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {mode === 'chat' ? (
              <div className="bg-white rounded-lg shadow-lg h-[600px] flex flex-col">
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.role === 'user'
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-lg p-3">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={loading}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={loading || !inputMessage.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg p-6">
                {!currentQuiz ? (
                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold text-gray-800">Generate a Quiz</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Topic
                      </label>
                      <input
                        type="text"
                        value={quizTopic}
                        onChange={(e) => setQuizTopic(e.target.value)}
                        placeholder="e.g., Python Loops, World War 2, Calculus"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty
                      </label>
                      <select
                        value={quizDifficulty}
                        onChange={(e) => setQuizDifficulty(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>

                    <button
                      onClick={generateQuiz}
                      disabled={loading || !quizTopic.trim()}
                      className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition font-medium"
                    >
                      {loading ? 'Generating Quiz...' : 'Generate Quiz'}
                    </button>
                  </div>
                ) : !quizResults ? (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-gray-800">{currentQuiz.topic}</h2>
                      <button
                        onClick={() => setCurrentQuiz(null)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        New Quiz
                      </button>
                    </div>

                    {currentQuiz.questions.map((q, idx) => (
                      <div key={idx} className="border-b pb-4">
                        <p className="font-medium text-gray-800 mb-3">
                          {idx + 1}. {q.question}
                        </p>
                        <div className="space-y-2">
                          {q.options.map((option) => {
                            const letter = option.charAt(0);
                            return (
                              <label
                                key={letter}
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  name={`question-${idx}`}
                                  value={letter}
                                  checked={quizAnswers[idx] === letter}
                                  onChange={(e) => setQuizAnswers({
                                    ...quizAnswers,
                                    [idx]: e.target.value
                                  })}
                                  className="text-indigo-600"
                                />
                                <span>{option}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={submitQuiz}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition font-medium"
                    >
                      {loading ? 'Submitting...' : 'Submit Quiz'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="text-center p-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg">
                      <Trophy className="w-16 h-16 mx-auto mb-2" />
                      <h2 className="text-3xl font-bold mb-2">Quiz Complete!</h2>
                      <p className="text-4xl font-bold">{quizResults.percentage}%</p>
                      <p className="text-lg">{quizResults.score} out of {quizResults.total} correct</p>
                    </div>

                    {quizResults.results.map((r, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg ${
                          r.isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        <p className="font-medium text-gray-800 mb-2">
                          {idx + 1}. {r.question}
                        </p>
                        <p className={r.isCorrect ? 'text-green-700' : 'text-red-700'}>
                          Your answer: {r.userAnswer} {r.isCorrect ? '✓' : '✗'}
                        </p>
                        {!r.isCorrect && (
                          <p className="text-green-700">Correct answer: {r.correctAnswer}</p>
                        )}
                        <p className="text-gray-600 mt-2 text-sm">{r.explanation}</p>
                      </div>
                    ))}

                    <button
                      onClick={() => {
                        setCurrentQuiz(null);
                        setQuizResults(null);
                        setQuizAnswers({});
                      }}
                      className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
                    >
                      Take Another Quiz
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3">Quick Tips</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Ask me to explain complex topics</li>
                <li>• Request examples and analogies</li>
                <li>• Generate quizzes to test yourself</li>
                <li>• Break down problems step-by-step</li>
                <li>• Get help with homework (no cheating!)</li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-3">Quiz History</h3>
              {quizHistory.length === 0 ? (
                <p className="text-sm text-gray-500">No quizzes yet</p>
              ) : (
                <div className="space-y-2">
                  {quizHistory.slice(-5).reverse().map((quiz) => (
                    <div
                      key={quiz.id}
                      className="p-3 bg-gray-50 rounded-lg text-sm"
                    >
                      <p className="font-medium text-gray-800">{quiz.topic}</p>
                      <p className="text-gray-600 text-xs">
                        {quiz.difficulty} • {quiz.completed ? `${quiz.score}/${quiz.total}` : 'In progress'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}